import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { signIn, signUp, resetPassword, updatePassword } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { z } from "zod";
import logo from "@/assets/logo.png";
import { sanitizeError } from "@/lib/errorUtils";
import { ArrowRight } from "lucide-react";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

const signupSchema = loginSchema.extend({
  name: z.string().min(2, "Name must be at least 2 characters").max(100, "Name too long"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const resetSchema = z.object({
  email: z.string().email("Invalid email address"),
});

const newPasswordSchema = z.object({
  password: z.string().min(6, "Password must be at least 6 characters"),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"auth" | "forgot" | "reset">("auth");
  
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [forgotForm, setForgotForm] = useState({ email: "" });
  const [resetForm, setResetForm] = useState({ password: "", confirmPassword: "" });

  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        if (searchParams.get("mode") === "reset") {
          setMode("reset");
        } else {
          navigate("/app");
        }
      }
    };
    checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        setMode("reset");
      } else if (session?.user && mode !== "reset") {
        navigate("/app");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, searchParams, mode]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      loginSchema.parse(loginForm);
      const { error } = await signIn(loginForm.email, loginForm.password);
      if (error) {
        toast.error(sanitizeError(error, "Login failed"));
      } else {
        toast.success("Welcome back!");
        navigate("/app");
      }
    } catch (err) {
      if (err instanceof z.ZodError) toast.error(err.errors[0].message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      signupSchema.parse(signupForm);
      const { error } = await signUp(signupForm.email, signupForm.password, signupForm.name);
      if (error) {
        toast.error(sanitizeError(error, "Failed to create account"));
      } else {
        toast.success("Account created!");
        navigate("/app");
      }
    } catch (err) {
      if (err instanceof z.ZodError) toast.error(err.errors[0].message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      resetSchema.parse(forgotForm);
      const { error } = await resetPassword(forgotForm.email);
      if (error) {
        toast.error(sanitizeError(error, "Failed to send reset link"));
      } else {
        toast.success("Password reset link sent");
        setMode("auth");
        setForgotForm({ email: "" });
      }
    } catch (err) {
      if (err instanceof z.ZodError) toast.error(err.errors[0].message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      newPasswordSchema.parse(resetForm);
      const { error } = await updatePassword(resetForm.password);
      if (error) {
        toast.error(sanitizeError(error, "Failed to update password"));
      } else {
        toast.success("Password updated");
        navigate("/app");
      }
    } catch (err) {
      if (err instanceof z.ZodError) toast.error(err.errors[0].message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Gradient background */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-radial from-primary/5 via-transparent to-transparent" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-radial from-accent/5 via-transparent to-transparent" />
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-accent rounded-2xl blur-xl opacity-30" />
                <img src={logo} alt="Memo" className="relative h-20 w-20 rounded-2xl shadow-xl" />
              </div>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Welcome to Memo</h1>
              <p className="text-muted-foreground mt-1">AI-powered KPI tracking</p>
            </div>
          </div>

          {mode === "forgot" && (
            <Card className="rounded-2xl">
              <CardHeader className="text-center pb-4">
                <CardTitle>Reset password</CardTitle>
                <CardDescription>We'll send you a reset link</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input id="forgot-email" type="email" placeholder="you@example.com" value={forgotForm.email} onChange={(e) => setForgotForm({ email: e.target.value })} required className="h-12 rounded-xl" />
                  </div>
                  <Button type="submit" className="w-full h-12 rounded-xl" disabled={loading}>
                    {loading ? "Sending..." : "Send reset link"}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full rounded-xl" onClick={() => setMode("auth")}>
                    Back to login
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {mode === "reset" && (
            <Card className="rounded-2xl">
              <CardHeader className="text-center pb-4">
                <CardTitle>Set new password</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="new-password">New password</Label>
                    <Input id="new-password" type="password" value={resetForm.password} onChange={(e) => setResetForm({ ...resetForm, password: e.target.value })} required className="h-12 rounded-xl" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-new-password">Confirm password</Label>
                    <Input id="confirm-new-password" type="password" value={resetForm.confirmPassword} onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })} required className="h-12 rounded-xl" />
                  </div>
                  <Button type="submit" className="w-full h-12 rounded-xl" disabled={loading}>
                    {loading ? "Updating..." : "Update password"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {mode === "auth" && (
            <Card className="rounded-2xl">
              <CardContent className="pt-6">
                <Tabs defaultValue="login" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 rounded-xl h-12 p-1">
                    <TabsTrigger value="login" className="rounded-lg">Login</TabsTrigger>
                    <TabsTrigger value="signup" className="rounded-lg">Sign up</TabsTrigger>
                  </TabsList>

                  <TabsContent value="login" className="mt-6">
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="login-email">Email</Label>
                        <Input id="login-email" type="email" placeholder="you@example.com" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} required className="h-12 rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="login-password">Password</Label>
                        <Input id="login-password" type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} required className="h-12 rounded-xl" />
                      </div>
                      <Button type="submit" className="w-full h-12 rounded-xl" disabled={loading}>
                        {loading ? "Logging in..." : "Log in"}
                      </Button>
                      <Button type="button" variant="link" className="w-full text-sm" onClick={() => setMode("forgot")}>
                        Forgot password?
                      </Button>
                    </form>
                  </TabsContent>

                  <TabsContent value="signup" className="mt-6">
                    <form onSubmit={handleSignup} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="signup-name">Full name</Label>
                        <Input id="signup-name" type="text" placeholder="Your name" value={signupForm.name} onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })} required className="h-12 rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-email">Email</Label>
                        <Input id="signup-email" type="email" placeholder="you@example.com" value={signupForm.email} onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })} required className="h-12 rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-password">Password</Label>
                        <Input id="signup-password" type="password" value={signupForm.password} onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })} required className="h-12 rounded-xl" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-confirm">Confirm password</Label>
                        <Input id="signup-confirm" type="password" value={signupForm.confirmPassword} onChange={(e) => setSignupForm({ ...signupForm, confirmPassword: e.target.value })} required className="h-12 rounded-xl" />
                      </div>
                      <Button type="submit" className="w-full h-12 rounded-xl" disabled={loading}>
                        <ArrowRight className="mr-2 h-4 w-4" />
                        {loading ? "Creating account..." : "Create account"}
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}

          <div className="text-center">
            <Button variant="link" onClick={() => navigate("/")}>Back to home</Button>
          </div>
        </div>
      </div>

      <footer className="py-4 text-center text-sm text-muted-foreground">
        Built by{" "}
        <a href="https://bwestudios.com" target="_blank" rel="noopener noreferrer" className="font-semibold text-foreground hover:text-primary transition-colors">
          BWE Studio
        </a>
      </footer>
    </div>
  );
};

export default Auth;
